import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { SIGNUP_CREDITS } from '../constants/plans';
import { db } from './firebase';
import { USER_TYPES, serializeDesignerProfile } from '../constants/designer';
import { serializeShopProfile } from '../constants/shop';
import { publishProfileCard } from './publicProfileService';
// Writing a user document makes any cached copy of it wrong. Importing the
// invalidator here keeps that obligation next to the writes themselves,
// rather than trusting every caller to remember.
import { invalidateDesigner } from './designerService';
import { invalidateShop } from './shopService';

// ── USER DOCUMENT SHAPE ──────────────────────────────────────────────────────
// users/{uid}
//   fullName, credits, plan, createdAt          ← every account
//   userType: 'customer'|'designer'|'shop'     ← chosen at signup
//   designer: { ... }                           ← designers only
//   shop: { ... }                               ← shops only
//   searchName                                  ← lowercased name, see below
//
// ONE DOCUMENT, NO MIRROR. Search and the public profile read users/{uid}
// directly, filtered on userType == 'designer'. An earlier version copied the
// public half into a separate `designers/{uid}` collection; that meant two
// documents to keep in step, and a profile whose copy failed to write simply
// vanished from search while looking perfectly fine to its owner.
//
// `email` IS DELIBERATELY NOT STORED HERE. Firestore grants read access per
// DOCUMENT, never per field, so everything on a designer's document is public
// the moment the directory is. Firebase Auth already holds the address and
// nothing in the app reads it from this record — leaving it off is what stops a
// public designer directory from doubling as an email list.
// Customer documents stay private; only designers are readable (firestore.rules).

// Lowercased name, stored alongside the display name. Search matches against
// this so "anna" finds "Anna" — comparing case-insensitively at query time is
// impossible in Firestore, and lowercasing on every client read wastes work
// that a single write can do once.
function toSearchName(fullName) {
  return String(fullName || '').trim().toLowerCase();
}

/**
 * Creates users/{uid} for a newly registered account. Document ID is the Auth
 * UID so Auth and Firestore stay in sync and any lookup is a direct get.
 *
 * THREE ROLES, ONE DOCUMENT. A designer carries a `designer` object, a shop a
 * `shop` object, a customer neither — but all three share `fullName`,
 * `avatarUrl` and `userType`, which is what lets every consumer that only
 * needs "who is this and what do they look like" (the header, comment
 * bylines, OwnerBadge, publicProfiles) stay completely unaware of roles.
 *
 * THE SHOP'S LOGO IS `avatarUrl`. It could have been `shop.logo`, and that
 * would have meant a parallel path through the avatar cropper, the public
 * profile card, the comment byline and the recommendation rail — four places
 * that already know how to show a round picture for a uid. A shop's logo is
 * that picture.
 *
 * @param {import('firebase/auth').User} user
 * @param {string} fullName
 * @param {'customer'|'designer'|'shop'} userType
 * @param {object|null} roleProfile  raw form state; ignored for customers
 */
export async function createUserDocument(user, fullName, userType = USER_TYPES.CUSTOMER, roleProfile = null, avatarUrl = '') {
  const isDesigner = userType === USER_TYPES.DESIGNER;
  const isShop = userType === USER_TYPES.SHOP;

  // The role profile's own name is the authoritative one — it was typed on the
  // profile step, which may differ from the account name field.
  const designer = isDesigner
    ? serializeDesignerProfile({ ...roleProfile, fullName: roleProfile?.fullName || fullName })
    : null;
  const shop = isShop
    ? serializeShopProfile({ ...roleProfile, name: roleProfile?.name || fullName })
    : null;

  const name = isDesigner ? designer.fullName
    : isShop ? shop.name
      : String(fullName || '').trim();

  await setDoc(doc(db, 'users', user.uid), {
    fullName:   name,
    // Written for every role that is searchable. It was previously set only by
    // the designer update path, which meant a designer created through
    // registration was missing from name search until they next saved.
    searchName: toSearchName(name),
    credits:    SIGNUP_CREDITS,
    plan:       'free',
    userType:   isDesigner ? USER_TYPES.DESIGNER : isShop ? USER_TYPES.SHOP : USER_TYPES.CUSTOMER,
    createdAt:  serverTimestamp(),
    // Every account type can have a picture; a designer's and a shop's arrive
    // on their profile object, a customer's is passed in directly.
    avatarUrl: (isDesigner || isShop ? roleProfile?.avatarUrl : avatarUrl) || '',
    ...(isDesigner && { designer }),
    ...(isShop && { shop }),
  });
}

/**
 * Google AND PHONE sign-in path — no type chosen yet, so default to customer.
 * The user can switch to a designer account from Settings, which fills in the
 * profile fields the one-step flows never asked for.
 *
 * THE PHONE NUMBER IS DELIBERATELY NOT STORED HERE, for exactly the reason the
 * email address is not (see the note at the top of this file): Firestore grants
 * read access per DOCUMENT, never per field, so anything written here is public
 * the moment the account becomes a designer or a shop. Firebase Auth already
 * holds the number as `user.phoneNumber`, and nothing in the app reads it from
 * this record — leaving it off is what stops a public directory from doubling
 * as a phone list.
 *
 * A PHONE ACCOUNT ARRIVES WITH NO NAME. There is no displayName on a phone
 * credential, so `fullName` starts empty and is filled in by whichever profile
 * form the chosen role leads to. A customer, who has no such form, sets it in
 * Settings — the same position a Google account with no display name is in.
 *
 * RETURNS WHETHER IT CREATED ANYTHING. The caller needs to tell a brand-new
 * account from a returning one, because only the new one has to be asked
 * customer-or-designer — and knowing it HERE costs nothing, where finding out
 * afterwards means a second read of the document just written.
 */
export async function createUserDocumentIfNew(user) {
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  if (snap.exists()) return false;

  await setDoc(userRef, {
    fullName:  user.displayName ?? '',
    credits:   SIGNUP_CREDITS,
    plan:      'free',
    // CUSTOMER is the safe default, not the answer. Signing in with Google
    // skips the registration form, so nobody has been asked which kind of
    // account they want — and silently making every Google user a customer
    // left designers with no way to become one at all.
    //
    // `userTypePending` is what says "not asked yet". The /account-type page
    // asks the question and clears the flag, and ProtectedRoute sends anyone
    // still carrying it there — so the answer survives a closed tab, and an
    // interrupted sign-up is asked again rather than silently defaulted.
    // Keeping a real value in `userType` meanwhile means every existing read
    // of it stays valid: if the page is never completed, the account simply
    // behaves as it did before.
    userType:  USER_TYPES.CUSTOMER,
    userTypePending: true,
    createdAt: serverTimestamp(),
  });
  return true;
}

/**
 * Records the account type chosen after a Google sign-up, and clears the
 * "not asked yet" flag so the chooser never appears again.
 *
 * A merge, not a set: the document already exists by this point and carries
 * credits, plan and createdAt that must survive.
 */
export async function setUserType(uid, userType) {
  // Validated against the list rather than a chain of equality checks, so a
  // fourth role later cannot be silently written through as 'customer'.
  const type = Object.values(USER_TYPES).includes(userType) ? userType : USER_TYPES.CUSTOMER;
  await setDoc(doc(db, 'users', uid), {
    userType: type,
    userTypePending: false,
  }, { merge: true });
  return type;
}

/**
 * Settings: save shop profile edits. The mirror of updateDesignerProfile, and
 * deliberately the same shape — one write to the document search reads, the
 * name denormalised to `fullName`/`searchName` so every role-agnostic consumer
 * keeps working, the logo to `avatarUrl`, and the public profile card
 * republished so comment bylines pick up a renamed shop.
 *
 * Writing `userType` here is what promotes a customer who chose "shop" later:
 * filling the profile in IS becoming one, exactly as it is for a designer.
 */
export async function updateShopProfile(uid, rawProfile, avatarUrl) {
  const profile = serializeShopProfile(rawProfile);
  const avatar = avatarUrl ?? rawProfile.avatarUrl ?? '';

  await setDoc(doc(db, 'users', uid), {
    userType:   USER_TYPES.SHOP,
    fullName:   profile.name,
    searchName: toSearchName(profile.name),
    shop:       profile,
    avatarUrl:  avatar,
  }, { merge: true });

  invalidateShop(uid);
  publishProfileCard(uid, { name: profile.name, avatarUrl: avatar });

  return { profile, published: true };
}

/** Settings: save designer profile edits. A single write — the document that
 *  search reads IS the document being edited. */
export async function updateDesignerProfile(uid, rawProfile, avatarUrl) {
  const profile = serializeDesignerProfile(rawProfile);
  const avatar = avatarUrl ?? rawProfile.avatarUrl ?? '';

  await setDoc(doc(db, 'users', uid), {
    userType:  USER_TYPES.DESIGNER,
    fullName:  profile.fullName,
    searchName: toSearchName(profile.fullName),
    designer:  profile,
    avatarUrl: avatar,
  }, { merge: true });

  invalidateDesigner(uid);
  publishProfileCard(uid, { name: profile.fullName, avatarUrl: avatar });

  // One document, so there is nothing that can fail to publish — the profile
  // IS what search reads. `published` stays in the return shape because callers
  // branch on it; it is now always true.
  return { profile, published: true };
}

/** Reads users/{uid}. Returns null when the document doesn't exist yet. */
export async function getUserDocument(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

/** Updates `fullName` (Settings). Merge-write, so credits/plan/createdAt are
 *  untouched. `searchName` rides along because Firestore cannot compare case-
 *  insensitively at query time. */
export async function updateUserFullName(uid, fullName) {
  const name = String(fullName || '').trim();
  await setDoc(doc(db, 'users', uid), {
    fullName: name,
    searchName: toSearchName(name),
  }, { merge: true });
  invalidateDesigner(uid);
}

/**
 * Saves just the profile picture (Settings). Used by customers, who have no
 * designer profile block to save it through. Merge-write so nothing else on
 * the document is touched.
 */
export async function updateUserAvatar(uid, avatarUrl) {
  await setDoc(doc(db, 'users', uid), { avatarUrl: avatarUrl || '' }, { merge: true });
  invalidateDesigner(uid);
  // The copy other people can read. A customer's own document is owner-only,
  // so without this their new picture is invisible everywhere but their own
  // screen — see publicProfileService.
  const me = await getUserDocument(uid).catch(() => null);
  publishProfileCard(uid, { name: me?.fullName || '', avatarUrl: avatarUrl || '' });
}

/**
 * Saves a CUSTOMER's location. Designers keep theirs inside `designer.location`
 * because it is part of their public card and is what search filters on; a
 * customer's is a plain account field, so it sits at the top level and no
 * directory query ever sees it.
 *
 * Coordinates are stored alongside the label so re-opening the form can drop
 * the map pin back where it was instead of re-geocoding the text.
 */
export async function updateUserLocation(uid, location, coords, address) {
  const short = String(location || '').trim();
  await setDoc(doc(db, 'users', uid), {
    location: short,
    // The full line the user actually picked. `location` stays coarse because
    // search groups on it; see LocationPicker for the whole reasoning.
    locationAddress: String(address || short).trim().slice(0, 250),
    locationCoords: coords?.lat != null ? { lat: coords.lat, lng: coords.lng } : null,
  }, { merge: true });
  invalidateDesigner(uid);
}
